// App.js
import './App.css';
import './styles/globals.css';
import './styles/components.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

// Import Context Providers
import { AuthProvider } from './context/AuthContext';
import { PointsProvider } from './context/PointsContext';
import { EventProvider } from './context/EventContext';

// Import Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Loading from './components/Loading';

// Import Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Volunteers from './pages/Volunteers';
import News from './pages/News';
import Suggestions from './pages/Suggestions';
import Rewards from './pages/Rewards';
import RewardVerification from './pages/RewardVerification';
import MemberCard from './pages/MemberCard';
import ConferenceRegistration from './pages/ConferenceRegistration';

// Import Admin Pages
import AdminLayout from './pages/AdminDashboard/AdminLayout';
import DashboardHome from './pages/AdminDashboard/DashboardHome';
import ManageEvents from './pages/AdminDashboard/ManageEvents';
import ManageMembers from './pages/AdminDashboard/ManageMembers';
import ManageVolunteers from './pages/AdminDashboard/ManageVolunteers';
import ManageNews from './pages/AdminDashboard/ManageNews';
import ManageRewards from './pages/AdminDashboard/ManageRewards';
import ManageGallery from './pages/AdminDashboard/ManageGallery';
import FixMembershipIDs from './pages/AdminDashboard/FixMembershipIDs';
import ManageConferences from './pages/AdminDashboard/ManageConferences';

function AppContent() {
  const location = useLocation();
  const hideHeaderFooter = ['/login', '/register'].includes(location.pathname) || location.pathname.startsWith('/member-card/');
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      {!hideHeaderFooter && !isAdminPage && <Navbar />}
      <main className={isAdminPage ? 'admin-main-wrapper' : ''}>
        {/* Routes */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-reward/:redemptionId" element={<RewardVerification />} />
          <Route path="/member-card/:userId" element={<MemberCard />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
          <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
          <Route path="/volunteers" element={<ProtectedRoute><Volunteers /></ProtectedRoute>} />
          <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
          <Route path="/suggestions" element={<ProtectedRoute><Suggestions /></ProtectedRoute>} />
          <Route path="/rewards" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
          <Route path="/conference-registration" element={<ProtectedRoute><ConferenceRegistration /></ProtectedRoute>} />
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<DashboardHome />} />
            <Route path="manage-events" element={<ManageEvents />} />
            <Route path="manage-members" element={<ManageMembers />} />
            <Route path="manage-volunteers" element={<ManageVolunteers />} />
            <Route path="manage-news" element={<ManageNews />} />
            <Route path="manage-rewards" element={<ManageRewards />} />
            <Route path="manage-conferences" element={<ManageConferences />} />
            <Route path="gallery" element={<ManageGallery />} />
            <Route path="fix-membership" element={<FixMembershipIDs />} />
          </Route>
        </Routes>
      </main>
      {!hideHeaderFooter && !isAdminPage && <Footer />}
    </div>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial loading time (adjust as needed)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500); // Show loading for 2.5 seconds

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthProvider>
      <PointsProvider>
        <EventProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppContent />
          </Router>
        </EventProvider>
      </PointsProvider>
    </AuthProvider>
  );
}

export default App;
